import localforage from 'localforage';

export class IndexedDbSyncService {
	private static instance: IndexedDbSyncService;

	public static getInstance(): IndexedDbSyncService {
		if (!IndexedDbSyncService.instance) {
			IndexedDbSyncService.instance = new IndexedDbSyncService();
		}
		return IndexedDbSyncService.instance;
	}

	private constructor() {}

	async saveState<T>(indexedDbKey: string, state: T): Promise<T> {
		const stateObject = this._prepareState(state);
		return await localforage.setItem(indexedDbKey, stateObject).catch(error => {
			return state;
		});
	}

	private _prepareState<T>(obj: T): T {
		return JSON.parse(
			JSON.stringify(obj, (key, value) => {
				return typeof value === 'function' ? undefined : value;
			}),
		);
	}

	async loadState<T>(indexedDbKey: string): Promise<T | null> {
		return await localforage.getItem<T>(indexedDbKey);
	}

	async clearState(indexedDbKey: string): Promise<void> {
		await localforage.removeItem(indexedDbKey);
	}

	async isEmpty(indexedDbKey: string): Promise<boolean> {
		const state = await this.loadState(indexedDbKey);
		return state === null;
	}

	async length(): Promise<number> {
		return await localforage.length();
	}

	async getSliceByKeys<T>(keys: (keyof T)[]): Promise<Partial<T>> {
		const state: Partial<T> = {};
		for (const key of keys) {
			state[key] = await this.loadState(key as string);
		}
		return state;
	}

	async bulkSet<T>(state: T): Promise<T> {
		for (const key in state) {
			await this.saveState(key, state[key]);
		}
		return Promise.resolve(state);
	}

	async clearAll(): Promise<void> {
		await localforage.clear();
	}

	async keys(): Promise<string[]> {
		return await localforage.keys();
	}

	async iterate<T>(iteratee: (value: T, indexedDbKey: string, iterationNumber: number) => void): Promise<void> {
		await localforage.iterate(iteratee);
	}

	async removeItem(indexedDbKey: string): Promise<void> {
		await localforage.removeItem(indexedDbKey);
	}

	async setItem(indexedDbKey: string, value: any): Promise<void> {
		await localforage.setItem(indexedDbKey, value);
	}

	async getItem<T>(indexedDbKey: string): Promise<T> {
		return await localforage.getItem(indexedDbKey);
	}

	async clear(): Promise<void> {
		await localforage.clear();
	}
}
